import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/common/SEOHead';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { sanitizeHtml } from '@/lib/sanitize';

const API_BASE = '';

export default function StaticPage({ pageSlug }) {
  const { slug: routeSlug } = useParams();
  const slug = pageSlug || routeSlug;
  const { currentLang } = useLanguage();
  const { t } = useTranslation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API_BASE}/api/pages/${slug}?lang=${currentLang}`)
      .then(r => r.json())
      .then(d => { if (!d.error) setPage(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, currentLang]);

  if (loading) return <div className="container mx-auto px-4 py-16 text-slate-400">Loading...</div>;
  if (!page) return <div className="container mx-auto px-4 py-16 text-slate-400">{t('common.notFound')}</div>;

  const faqJsonLd = page.faq_items?.length > 0 ? [{
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": page.faq_items.map(q => ({
      "@type": "Question", "name": q.question,
      "acceptedAnswer": {"@type": "Answer", "text": q.answer}
    }))
  }] : null;

  return (
    <div className="min-h-[60vh]" data-testid="static-page">
      <SEOHead
        title={page.meta_title || page.title}
        description={page.meta_description}
        indexable={page.is_indexable !== false}
        ogTitle={page.og_title}
        ogDescription={page.og_description}
        jsonLd={faqJsonLd}
      />

      {/* Hero */}
      <div className="border-b border-slate-800/50 bg-gradient-to-b from-slate-900/80 to-transparent">
        <div className="max-w-3xl mx-auto px-4 pt-10 pb-8">
          <nav className="flex items-center gap-2 text-xs text-slate-500 mb-6" data-testid="breadcrumb">
            <Link to={`/${currentLang}`} className="hover:text-slate-300">{t('nav.home')}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-400">{page.title}</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-100 tracking-tight mb-2"
            style={{ fontFamily: 'var(--font-heading)' }} data-testid="page-title">
            {page.title}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-10">
        {page.content_html && (
          <article
            className="static-content"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.content_html) }}
            data-testid="page-content"
          />
        )}

        {/* FAQ */}
        {page.faq_items && page.faq_items.length > 0 && (
          <div className="mt-12 space-y-3" data-testid="page-faq">
            <h2 className="text-xl md:text-2xl font-semibold text-slate-100 border-l-4 border-emerald-500 pl-3 mb-6"
              style={{ fontFamily: 'var(--font-heading)' }}>
              {t('seo.faq', 'FAQ')}
            </h2>
            {page.faq_items.map((item, i) => (
              <FAQAccordion key={i} question={item.question} answer={item.answer} index={i} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 border border-emerald-500/20 bg-emerald-500/5 rounded-xl p-6 text-center" data-testid="page-cta">
          <p className="text-sm text-slate-300 mb-3">{t('cta.compareOdds', '30+ bahisçide canlı oran karşılaştırması başlat')}</p>
          <Link to={`/${currentLang}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-colors"
            data-testid="cta-button">
            {t('cta.explore', 'Explore ODDSIS')}
          </Link>
        </div>
      </div>

      {/* Scoped styles for static content */}
      <style>{`
        .static-content h2 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #f1f5f9;
          margin-top: 2.5rem;
          margin-bottom: 1rem;
          padding-left: 0.75rem;
          border-left: 4px solid #10b981;
          font-family: var(--font-heading);
        }
        @media (min-width: 768px) {
          .static-content h2 { font-size: 1.5rem; }
        }
        .static-content h2:first-child { margin-top: 0; }
        .static-content h3 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #cbd5e1;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          padding-left: 0.75rem;
          border-left: 4px solid #334155;
          font-family: var(--font-heading);
        }
        .static-content p {
          font-size: 0.9375rem;
          color: #94a3b8;
          line-height: 1.75;
          margin-bottom: 0.75rem;
        }
        .static-content strong {
          color: #e2e8f0;
          font-weight: 600;
        }
        .static-content ul {
          list-style: none;
          padding: 0;
          margin: 0.75rem 0;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .static-content ul li {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          font-size: 0.9375rem;
          color: #94a3b8;
          line-height: 1.6;
        }
        .static-content ul li::before {
          content: "✓";
          color: #10b981;
          font-weight: 700;
          flex-shrink: 0;
          margin-top: 0.1rem;
        }
        .static-content a {
          color: #34d399;
          text-decoration: none;
        }
        .static-content a:hover { text-decoration: underline; }
      `}</style>
    </div>
  );
}

function FAQAccordion({ question, answer, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-800/50 rounded-lg overflow-hidden" data-testid={`static-faq-${index}`}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-800/20 transition-colors">
        <span className="text-sm text-slate-200 font-medium pr-4">{question}</span>
        <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 border-t border-slate-800/30">
          <p className="text-sm text-slate-400 leading-relaxed pt-3">{answer}</p>
        </div>
      )}
    </div>
  );
}
